import { useTranslations } from "next-intl";
import { useState, useRef, KeyboardEvent, ClipboardEvent } from "react";
import styled from "styled-components";
import Button from "../Button/Button";
import { useQueryClient } from "@tanstack/react-query";
import {
  CodeMismatchException,
  ExpiredCodeException,
  LimitExceededException,
} from "@aws-sdk/client-cognito-identity-provider";
import { useAuth } from "react-oidc-context";
import toast from "react-hot-toast";
import { useMedia } from "@/lib/hooks/useMedia";
import CustomToast from "../CustomToast/CustomToast";
import { useCognitoApi } from "@/lib/hooks/useCognitoApi";
import theme from "@/themes/velys-theme";

interface OTPVerificationProps {
  closeModal: () => void;
  sendOTPCode: (trigger?: string) => void;
  isResendDisabled: boolean;
}

const OTPVerificationContainer = styled.div<{ $disabled?: boolean }>`
  border-radius: 16px;
  width: 100%;

  opacity: ${(props) => (props.$disabled ? 0.4 : 1)};
  pointer-events: ${(props) => (props.$disabled ? "none" : "auto")};
`;

const Description = styled.p`
  margin-bottom: 32px;
`;

const OTPContainer = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  align-items: center;
  margin-bottom: 32px;
`;

const OTPInput = styled.input<{ $isFilled: boolean }>`
  width: clamp(44px, 8vw, 88px);
  height: clamp(74px, 13vw, 147px);
  font-size: clamp(20px, 2.5vw, 50px);
  font-weight: 600;
  text-align: center;
  border: 1px solid
    ${(props) => (props.$isFilled ? "#1f2937" : props.theme.responseBorder)};
  border-radius: 15px;
  outline: none;
  transition: all 0.2s ease;
  background-color: ${(props) => props.theme.backgroundGrey};
  color: #1f2937;
  caret-color: transparent;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  @media (max-width: 768px) {
    border-radius: 5px;
  }
`;

const Dash = styled.span`
  font-size: 32px;
  font-weight: 600;
  color: #000000;
`;

const OTPVerificationFooter = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;

  .action-buttons {
    display: flex;
    gap: 10px;
  }
`;

export default function OTPVerification({
  closeModal,
  sendOTPCode,
  isResendDisabled,
}: OTPVerificationProps) {
  const tComponentText = useTranslations("componentText");
  const queryClient = useQueryClient();

  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const max768w = useMedia("(max-width: 768px)");

  const { verifyOTPCodeMutation } = useCognitoApi();

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (otp[index]) {
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }
    if (e.key === "ArrowLeft" && index > 0)
      inputRefs.current[index - 1]?.focus();
    if (e.key === "ArrowRight" && index < 5)
      inputRefs.current[index + 1]?.focus();
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    const newOtp = [...otp];
    for (let i = 0; i < pasted.length; i++) newOtp[i] = pasted[i];
    setOtp(newOtp);

    const nextIndex = Math.min(pasted.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleCancelVerification = () => {
    setOtp(Array(6).fill(""));
    closeModal();
  };

  const handleVerifyCode = () => {
    const otpCode = otp.join("");

    verifyOTPCodeMutation.mutate(otpCode, {
      onSuccess: () => {
        toast.custom((t) => (
          <CustomToast
            t={t}
            message={tComponentText("successVerification")}
            success
          />
        ));
        queryClient.invalidateQueries({ queryKey: ["get-user-profile"] });
        closeModal();
      },

      onError: (error) => {
        if (error instanceof LimitExceededException) {
          toast.custom((t) => (
            <CustomToast
              t={t}
              message={tComponentText("errorVerificationMax")}
            />
          ));
        } else if (error instanceof CodeMismatchException) {
          toast.custom((t) => (
            <CustomToast t={t} message={tComponentText("errorInvalidCode")} />
          ));
        } else if (error instanceof ExpiredCodeException) {
          toast.custom((t) => (
            <CustomToast t={t} message={tComponentText("errorExpiredCode")} />
          ));
        } else {
          toast.custom((t) => (
            <CustomToast
              t={t}
              message={tComponentText("errorVerificationFailed")}
            />
          ));
        }
      },

      onSettled: () => {
        setOtp(Array(6).fill(""));
      },
    });
  };

  const handleResendCode = () => {
    sendOTPCode("trigger");
  };

  return (
    <OTPVerificationContainer $disabled={verifyOTPCodeMutation.isPending}>
      <Description>{tComponentText("otpInstruction")}</Description>

      <OTPContainer>
        {otp.map((digit, index) => (
          <span
            key={index}
            style={{ display: "flex", alignItems: "center", gap: 10 }}
          >
            <OTPInput
              ref={(el) => {
                inputRefs.current[index] = el;
                return;
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              placeholder="–"
              $isFilled={!!digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
            />
            {index === 2 && <Dash>–</Dash>}
          </span>
        ))}
      </OTPContainer>

      <OTPVerificationFooter>
        <Button
          label={
            max768w
              ? tComponentText("cancel")
              : tComponentText("cancelVerification")
          }
          name="cancel-verification"
          theme="tertiary"
          onPress={handleCancelVerification}
        />
        <div className="action-buttons">
          <Button
            label={
              max768w ? tComponentText("resend") : tComponentText("resendCode")
            }
            name="resend-code"
            theme="secondary"
            onPress={handleResendCode}
            isDisabled={isResendDisabled || verifyOTPCodeMutation.isPending}
          />
          <Button
            label={tComponentText("verify")}
            name="verify"
            theme="primary"
            onPress={handleVerifyCode}
            isDisabled={
              otp.join("").length < 6 || verifyOTPCodeMutation.isPending
            }
            icon={verifyOTPCodeMutation.isPending ? "loader" : undefined}
            iconColor={
              verifyOTPCodeMutation.isPending ? theme.white : undefined
            }
          />
        </div>
      </OTPVerificationFooter>
    </OTPVerificationContainer>
  );
}
